import React, { useState, useEffect, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { Button, Card, Checkbox, Divider, InputNumber, Select, Space, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useProductList } from '../../../hooks/productSetup';
import { useCategoryList } from '../../../hooks/categorySetup';
import { useConfigOptionList } from '../../../hooks/configOption';
import { IDisplayCondition, IDependency } from '../../../types/displayCondition';

interface DisplayConditionFormProps {
  initialCondition?: IDisplayCondition;
}

export interface DisplayConditionFormRef {
  getCondition: () => IDisplayCondition;
}

export const DisplayConditionForm = forwardRef<DisplayConditionFormRef, DisplayConditionFormProps>(
  ({ initialCondition }, ref) => {
    const [enabled, setEnabled] = useState(initialCondition?.enabled ?? true);
    const [dependencies, setDependencies] = useState<IDependency[]>(
      initialCondition?.dependsOn || []
    );
    const [validForProducts, setValidForProducts] = useState<string[]>(
      initialCondition?.validForProducts || []
    );
    const [notValidForProducts, setNotValidForProducts] = useState<string[]>(
      initialCondition?.notValidForProducts || []
    );
    const [widthMin, setWidthMin] = useState<number | undefined>(
      initialCondition?.widthMin
    );
    const [widthMax, setWidthMax] = useState<number | undefined>(
      initialCondition?.widthMax
    );

    // Fetch data
    const { data: productsData } = useProductList();
    const { data: categoriesData } = useCategoryList();

    // Memoize products and categories to prevent re-renders
    const products = useMemo(() => productsData?.value || [], [productsData?.value]);
    const categories = useMemo(() => categoriesData?.value || [], [categoriesData?.value]);

    // Reset state when initialCondition changes (when switching between different options)
    useEffect(() => {
      setEnabled(initialCondition?.enabled ?? true);
      setDependencies(initialCondition?.dependsOn || []);
      setValidForProducts(initialCondition?.validForProducts || []);
      setNotValidForProducts(initialCondition?.notValidForProducts || []);
      setWidthMin(initialCondition?.widthMin);
      setWidthMax(initialCondition?.widthMax);
    }, [initialCondition]);

    // Expose method to get current condition
    useImperativeHandle(ref, () => ({
      getCondition: () => ({
        enabled,
        dependsOn: dependencies,
        validForProducts,
        notValidForProducts,
        ...(widthMin !== undefined && { widthMin }),
        ...(widthMax !== undefined && { widthMax }),
      }),
    }));

  const addDependency = () => {
    const newDependency: IDependency = {
      categoryId: '',
      optionIds: [],
    };
    setDependencies([...dependencies, newDependency]);
  };

  const removeDependency = (index: number) => {
    setDependencies(dependencies.filter((_, i) => i !== index));
  };

  const updateDependency = (index: number, field: keyof IDependency, value: any) => {
    const updated = [...dependencies];
    const current = updated[index];
    if (current) {
      updated[index] = { ...current, [field]: value } as IDependency;

      // Reset optionIds when category changes
      if (field === 'categoryId') {
        updated[index]!.optionIds = [];
      }
    }

    setDependencies(updated);
  };

  const handleSelectAllProducts = useCallback(() => {
    if (validForProducts.length === products.length) {
      setValidForProducts([]);
    } else {
      setValidForProducts(products.map((p) => p.productid));
    }
  }, [validForProducts.length, products]);


  return (
    <div className="pt-2">
      <Divider className="my-3">Display Conditions</Divider>

      <div className="mb-3">
        <Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)}>
          Enable Display Conditions
        </Checkbox>
        <div className="text-xs text-gray-500 mt-1">
          Control when this option should be displayed based on other selections
        </div>
      </div>

      {enabled && (
        <>
          {/* Dependencies Section */}
          <div className="mb-4">
            <div className="text-sm font-semibold mb-2">Depends On:</div>
            <div className="text-xs text-gray-500 mb-3">
              This option will only show when the selected options are chosen
            </div>

            {dependencies.map((dependency, index) => (
              <Card
                size="small"
                key={index}
                className="mb-2"
                extra={
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeDependency(index)}
                  />
                }
              >
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Space wrap>
                    <span className="text-sm font-medium">When Category:</span>
                    <Select
                      size="small"
                      placeholder="Select category"
                      value={dependency.categoryId || undefined}
                      onChange={(value) => updateDependency(index, 'categoryId', value)}
                      style={{ width: 200 }}
                    >
                      {categories.map((category) => (
                        <Select.Option key={category.dogma_categoryid} value={category.dogma_categoryid}>
                          {category.dogma_name}
                        </Select.Option>
                      ))}
                    </Select>

                    <span className="text-sm font-medium">Has Options:</span>
                    <DependencyOptionSelector
                      dependency={dependency}
                      index={index}
                      updateDependency={updateDependency}
                    />
                  </Space>
                </Space>
              </Card>
            ))}

            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={addDependency}
            >
              Add Dependency
            </Button>

            {dependencies.length === 0 && (
              <div className="text-center py-3 text-gray-400 text-xs mt-2">
                No dependencies added. This option will always be visible (for selected products).
              </div>
            )}
          </div>

          <Divider className="my-3" />

          {/* Valid For Products Section */}
          <div className="mb-3">
            <div className="flex justify-between items-center mb-2">
              <div>
                <div className="text-sm font-semibold">Valid For Products:</div>
                <div className="text-xs text-gray-500 mt-1">
                  Select which products this option is applicable to
                </div>
              </div>
              <Button size="small" onClick={handleSelectAllProducts}>
                {validForProducts.length === products.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            <Card size="small" className="bg-gray-50">
              <Checkbox.Group
                value={validForProducts}
                onChange={(checkedValues) => setValidForProducts(checkedValues as string[])}
                style={{ width: '100%' }}
              >
                <div className="grid grid-cols-2 gap-2">
                  {products.map((product) => (
                    <Checkbox key={product.productid} value={product.productid}>
                      {product.name}
                    </Checkbox>
                  ))}
                </div>
              </Checkbox.Group>

              {products.length === 0 && (
                <div className="text-center py-3 text-gray-400 text-xs">
                  No products available
                </div>
              )}
            </Card>
          </div>

          <Divider className="my-3" />

          {/* Width Restrictions Section */}
          <div className="mb-3">
            <div className="text-sm font-semibold mb-1">Width Restrictions (mm):</div>
            <div className="text-xs text-gray-500 mb-3">
              Only show this option when the window width is within the specified range. Leave blank for no restriction.
            </div>
            <Space>
              <div>
                <div className="text-xs text-gray-600 mb-1">Min Width (mm)</div>
                <InputNumber
                  size="small"
                  min={0}
                  max={9999}
                  placeholder="e.g. 520"
                  value={widthMin}
                  onChange={(val) => setWidthMin(val ?? undefined)}
                  style={{ width: 130 }}
                />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Max Width (mm)</div>
                <InputNumber
                  size="small"
                  min={0}
                  max={9999}
                  placeholder="e.g. 749"
                  value={widthMax}
                  onChange={(val) => setWidthMax(val ?? undefined)}
                  style={{ width: 130 }}
                />
              </div>
            </Space>
            {(widthMin !== undefined || widthMax !== undefined) && (
              <div className="text-xs text-blue-600 mt-2">
                This option shows for widths:{' '}
                {widthMin !== undefined ? `≥ ${widthMin}mm` : ''}
                {widthMin !== undefined && widthMax !== undefined ? ' and ' : ''}
                {widthMax !== undefined ? `≤ ${widthMax}mm` : ''}
              </div>
            )}
          </div>
        </>
      )}
    </div>
    );
  }
);

DisplayConditionForm.displayName = 'DisplayConditionForm';

// Helper component for dependency option selection
const DependencyOptionSelector: React.FC<{
  dependency: IDependency;
  index: number;
  updateDependency: (index: number, field: keyof IDependency, value: any) => void;
}> = ({ dependency, index, updateDependency }) => {
  const { data: optionsData } = useConfigOptionList(dependency.categoryId);
  const options = optionsData || [];

  const getOptionTypeName = (option: any): string =>
    option["dogma_optiontype@OData.Community.Display.V1.FormattedValue"] || "";

  const getOptionLabel = (option: any): string => {
    const typeName = getOptionTypeName(option);
    return typeName ? `${typeName} - ${option.dogma_name}` : option.dogma_name;
  };

  return (
    <Select
      mode="multiple"
      size="small"
      placeholder={dependency.categoryId ? 'Select options' : 'Select category first'}
      value={dependency.optionIds}
      onChange={(value) => updateDependency(index, 'optionIds', value)}
      style={{ minWidth: 250 }}
      disabled={!dependency.categoryId}
      showSearch
      filterOption={(input, option) =>
        (option?.label?.toString() || '').toLowerCase().includes(input.toLowerCase())
      }
    >
      {options.map((option) => {
        const typeName = getOptionTypeName(option);
        return (
          <Select.Option
            key={option.dogma_configurationoptionsid}
            value={option.dogma_configurationoptionsid}
            label={getOptionLabel(option)}
          >
            <Space size={4}>
              {typeName && (
                <Tag color="blue" style={{ fontSize: 11, padding: '0 4px', lineHeight: '16px' }}>
                  {typeName}
                </Tag>
              )}
              {option.dogma_name}
            </Space>
          </Select.Option>
        );
      })}
    </Select>
  );
};
